import React, { FC, useMemo, useRef } from 'react'
import { Link, type RouteObject } from 'react-router-dom'
import { useUser } from '@clerk/clerk-react'
import './default.css'

// COMPONENTS
import * as Icons from '@icons'
import { Column } from '@components/templates/column'
import UserResourceStatusCell from '../_components/user-attribute-status-cell'
import Avatar from '@components/molecules/avatar'
import Card from '@components/atoms/card'
import { priceFormatter } from '@/utils/number-helper'


const Component: FC<{}> = () => {
    const { isSignedIn, user } = useUser()
    const fileInputRef = useRef<HTMLInputElement>(null)

    const userFullName = useMemo(() => `${user?.firstName} ${user?.lastName}`.trim(), [user?.firstName, user?.lastName])

    function changeProfilePicture() {
        fileInputRef.current?.click()
    }

    function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
        const { name, files } = e.currentTarget
        if (name === 'file-input' && files && files[0]) updateUserProfileImage(files[0])
    }

    function updateUserProfileImage(image: File) {
        if (!isSignedIn || !user.id) return;
        user?.setProfileImage({ file: image })
            .catch((err) => { console.error(err) })
    }

    return (<>
        <h1>Profile</h1>
        <div className="user-details flex flex-row items-center">
            <Avatar user={user} />
            <h4 className='flex-1'>{userFullName}</h4>
            <a className='text-blue-500' onClick={changeProfilePicture}>Change Image</a>
            <input ref={fileInputRef} type="file" name="file-input" accept="image/png, image/jpeg" onChange={handleChange} hidden />
        </div>

        {/* Referral Balance Section */}
        <div style={{
            background: 'linear-gradient(135deg, #20c997 0%, #17a2b8 100%)',
            borderRadius: 'var(--border-radius-lrg)',
            padding: '1.3rem',
            color: 'var(--splitcard-white)',
        }}>
            <div className="flex flex-row items-stretch justify-between">
                <div className="flex flex-col justify-between gap-8 flex-1">
                    <div className='flex flex-col gap-4'>
                        <div className='flex flex-row items-center gap-2'>
                            <img src="/splitcard/symbol/white.svg" alt="Splitcard Symbol" className='w-5 h-5' />
                            <p className="text-body-2"><strong>CREDIT</strong></p>
                        </div>
                        <h2 className='text-[2.5rem] font-bold'>{priceFormatter.format((user?.publicMetadata?.creditAmount || 0) / 100)}</h2>
                    </div>
                    <p className="text-body-2">You can apply it when creating any new group.</p>
                </div>
                <div className="bubble-xxsml flex items-center justify-center !bg-white/20 !rounded-[5px]">
                    <Icons.GiftBox width="1.5rem" height="1.5rem" />
                </div>
            </div>
        </div>

        <Card className='gap-2'>
            <div className="flex flex-row items-center justify-between">
                <h4>E-mail Address</h4>
                {(!user?.emailAddresses || user?.emailAddresses.length === 0)
                    ? <Link className='text-blue-500' to="./email/">Add</Link>
                    : <Link className='text-blue-500' to={`./email/${user?.emailAddresses?.at(0)?.id || ''}/update`}>Update</Link>
                }
            </div>
            {(!!user?.emailAddresses.length) && user.emailAddresses.map((email, index, arr) => (
                <React.Fragment key={`${email.id}-${index}`}>
                    <UserResourceStatusCell resource={email} />
                    {(index !== arr.length - 1) && <hr />}
                </React.Fragment>
            ))}
        </Card>
        <Card className='gap-2'>
            <h4>Phone Number</h4>
            {(!!user?.phoneNumbers.length) ? user.phoneNumbers.map((phoneNumber, index, arr) => (
                <React.Fragment key={`${phoneNumber.id}-${index}`}>
                    <UserResourceStatusCell resource={phoneNumber} />
                    {(index !== arr.length - 1) && <hr />}
                </React.Fragment>
            )) : (<h3>Empty</h3>)}
        </Card>
        <Card >
            <div className='flex flex-row items-center justify-between'>
                <div className='flex flex-col'>
                    <h4>Notification Settings</h4>
                    <p>Text/SMS and Push</p>
                </div>
                {(!!user?.phoneNumbers.length) ? (
                    <Link to='./sms-settings' className='flex flex-row items-center justify-between text-black'>
                        <Icons.Chevron width="1.25rem" height="1.25rem" facing="right" />
                    </Link>
                ) : ((<h3>Empty</h3>))}
            </div>
        </Card>
        <Column.Footer hidden />
    </>)
}

const DefaultRoute: RouteObject = {
    index: true,
    Component,
}

export { DefaultRoute }
